import { useState, useEffect, useRef } from "react";
import { PenLine, Loader2, BadgeCheck, Stamp } from "lucide-react";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import api from "@/lib/api";
import { SignaturePad } from "@/components/webadvisor/SignaturePad";

const errMsg = (err, f) => { const d = err.response?.data?.detail; return Array.isArray(d) ? d.map(e => e.msg).join(", ") : d || f; };

const SaveSignature = ({ me, onSaved, onCancel }) => {
  const pad = useRef(null);
  const [name, setName] = useState(me.name || "");
  const [title, setTitle] = useState(me.title || "");
  const [drawn, setDrawn] = useState(false);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { const { data } = await api.put("/api/me/signature", { name, title, signature: pad.current.toDataURL() }); toast.success("Signature saved"); onSaved(data); }
    catch (err) { toast.error(errMsg(err, "Couldn't save signature")); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-2" data-testid="save-signature">
      <div className="grid sm:grid-cols-2 gap-2">
        <Input value={name} onChange={e => setName(e.target.value)} placeholder="Your full name" className="bg-white" data-testid="my-sig-name" />
        <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="Title (e.g. Account Manager)" className="bg-white" data-testid="my-sig-title" />
      </div>
      <SignaturePad ref={pad} onChange={setDrawn} />
      <div className="flex gap-2">
        <button onClick={save} disabled={busy || name.trim().length < 2 || !drawn} className="bg-slate-900 hover:bg-slate-700 text-white text-sm font-bold px-4 py-2 rounded-lg flex items-center gap-1.5 disabled:opacity-50" data-testid="my-sig-save">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <PenLine className="w-4 h-4" />}Save my signature</button>
        {onCancel && <button onClick={onCancel} className="text-sm text-slate-500 px-2" data-testid="my-sig-cancel">Cancel</button>}
      </div>
      <p className="text-xs text-slate-400">Saved to your account and reused for every contract you countersign.</p>
    </div>
  );
};

export const Countersign = ({ planId, signature, countersignature, onDone }) => {
  const [me, setMe] = useState(null);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [v, setV] = useState(0);
  useEffect(() => { api.get("/api/me/signature").then(r => setMe(r.data)).catch(() => {}); }, []);

  if (countersignature) return (
    <p className="text-xs text-green-700 font-semibold flex items-center gap-1.5" data-testid="countersign-done"><BadgeCheck className="w-4 h-4" />Countersigned by {countersignature.name}{countersignature.title ? `, ${countersignature.title}` : ""} · {countersignature.signed_at.slice(0, 10)}{countersignature.emailed_to ? ` · signed copy emailed to ${countersignature.emailed_to}` : ""}</p>
  );
  if (!signature || !me) return null;
  const sign = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/api/webadvisor/plan/${planId}/countersign`, { origin_url: window.location.origin }); toast.success(data.emailed ? `Countersigned — signed copy emailed to ${data.emailed_to}` : "Countersigned"); onDone(); }
    catch (err) { toast.error(errMsg(err, "Countersign failed")); } finally { setBusy(false); }
  };
  return (
    <div className="bg-amber-50/70 border border-amber-200 rounded-lg p-3 space-y-3" data-testid="countersign-box">
      <p className="text-sm font-bold text-slate-900 flex items-center gap-2"><Stamp className="w-4 h-4 text-amber-600" />Customer signed — countersign for ShopAdvisor&trade;</p>
      {!me.has_signature || editing ? (
        <SaveSignature me={me} onCancel={me.has_signature ? () => setEditing(false) : null} onSaved={(d) => { setMe(d); setEditing(false); setV(x => x + 1); }} />
      ) : (
        <div className="flex flex-wrap items-center gap-3">
          <img src={`${process.env.REACT_APP_BACKEND_URL}/api/me/signature.png?v=${v}${me.updated_at || ""}`} alt="Your signature" className="h-12 w-[180px] object-contain bg-white rounded border border-amber-100 px-2" data-testid="my-sig-preview" />
          <div className="text-sm"><p className="font-semibold text-slate-900">{me.name}</p>{me.title && <p className="text-xs text-slate-500">{me.title}</p>}</div>
          <button onClick={sign} disabled={busy} className="bg-green-600 hover:bg-green-500 text-white text-sm font-bold px-4 py-2 rounded-lg flex items-center gap-1.5 disabled:opacity-50" data-testid="countersign-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <PenLine className="w-4 h-4" />}Countersign &amp; email signed copy</button>
          <button onClick={() => setEditing(true)} className="text-xs text-blue-600 hover:underline" data-testid="my-sig-change">Change my signature</button>
        </div>
      )}
    </div>
  );
};
